import { ReactNode, createContext, useContext } from 'react'
import { UnswAuthProvider, useUnswAuth } from './UnswAuthProvider'

// Lx roles
export enum Role {
  None = '',
  Author = 'LxAuthor',
  Designer = 'LxDesigner',
  Admin = 'LxAdmin',
  SuperAdmin = 'LxSuperAdmin',
  Contractor = 'LxContractor',
}

export enum Permission {
  // authoring
  CreateAuthoring = 'create:lx_authoring',
  PublishAuthoring = 'publish:lx_authoring',
  // sourcing
  ReadSouring = 'read:lx_sourcing',
  // topics
  CreateCalalog = 'create:lx_catalog',
  UpdateCatalog = 'update:lx_catalog',
  ReadCatalog = 'read:lx_catalog',
  DeleteCatalog = 'delete:lx_catalog',
  // media
  CreateImages = 'create:lx_images',
  UpdateImages = 'update:lx_images',
  ReadImages = 'read:lx_images',
  DeleteImages = 'delete:lx_images',
  // quizzes
  CreateQuizzes = 'create:lx_quizzes',
  UpdateQuizzes = 'update:lx_quizzes',
  ReadQuizzes = 'read:lx_quizzes',
  DeleteQuizzes = 'delete:lx_quizzes',
  // architectures
  CreateArchitectures = 'create:lx_architectures',
  UpdateArchitectures = 'update:lx_architectures',
  ReadArchitectures = 'read:lx_architectures',
  DeleteArchitectures = 'delete:lx_architectures',
  // grades
  CreateGrades = 'create:lx_grades',
  UpdateGrades = 'update:lx_grades',
  ReadGrades = 'read:lx_grades',
  DeleteGrades = 'delete:lx_grades',
}

export enum LocalStorageEnum {
  TARGET_URL = 'targetUrl', // for redirecting after login
}

export const setTargetUrl = (url: string) => {
  localStorage.setItem(LocalStorageEnum.TARGET_URL, url)
}

export const getTargetUrl = () => {
  const targetUrl = localStorage.getItem(LocalStorageEnum.TARGET_URL)
  return targetUrl ? targetUrl : window.location.origin
}

export const deleteTargetUrl = () => {
  localStorage.removeItem(LocalStorageEnum.TARGET_URL)
}

interface CompositeAuthProviderProps {
  children: ReactNode
}

interface AuthContextProps {
  getAccessTokenSilently: () => Promise<string>
  getRole: () => Role
  isAuthenticated: boolean
  isLogged: boolean // alias for isAuthenticated, used for compatibility
  isLoading: boolean
  user: any
  login: (options?: any) => Promise<void>
  logout: (options?: any) => void
  permissions: string[]
  hasPermission: (permission: Permission, id?: string) => boolean
  withAuthenticationRequired: any
  error?: Error | undefined
}

export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined,
)

export const CompositeAuthProvider = ({
  children,
}: CompositeAuthProviderProps) => {
  return (
    <UnswAuthProvider>
      <UnswWrapper>{children}</UnswWrapper>
    </UnswAuthProvider>
  )
}

const UnswWrapper = ({ children }: { children: ReactNode }) => {
  const unsw = useUnswAuth()

  const hasPermissionWithOwnership = (
    permission: Permission,
    resourceOwnerId?: string,
  ): boolean => {
    // First check if user has the base permission
    if (!unsw.hasPermission(permission)) {
      return false
    }

    // If no resourceOwnerId is provided, just check the permission
    if (!resourceOwnerId) {
      return true
    }

    // SuperAdmin can access all resources
    const role = unsw.getRole()
    if (role === Role.SuperAdmin) {
      return true
    }

    // For other roles, check if they own the resource
    return unsw.user?.id === resourceOwnerId
  }

  const contextValue: AuthContextProps = {
    getAccessTokenSilently: unsw.getAccessTokenSilently,
    getRole: unsw.getRole,
    isAuthenticated: unsw.isAuthenticated,
    isLogged: unsw.isAuthenticated,
    isLoading: unsw.isLoading,
    user: unsw.user,
    error: unsw.error,
    login: unsw.loginWithRedirect,
    logout: unsw.logout,
    permissions: unsw.user?.permissions || [],
    hasPermission: hasPermissionWithOwnership,
    withAuthenticationRequired: unsw.withAuthenticationRequired,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export const useCompositeAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error(
      'useCompositeAuth must be used within a CompositeAuthProvider',
    )
  }
  return context
}
